import { useEffect, useState } from "react";
import { Clock, Percent, DollarSign } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";

interface Promotion {
  id: string;
  title: string;
  description?: string | null;
  discount_percentage?: number | null;
  discount_amount?: number | null;
  start_date: string;
  end_date: string;
  is_active: boolean;
  image_url?: string | null;
}

interface PromotionCountdownProps {
  promotion: Promotion;
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

export default function PromotionCountdown({ promotion }: PromotionCountdownProps) {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isExpired, setIsExpired] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Trigger entrance animation
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const endDate = new Date(promotion.end_date).getTime();
      const difference = endDate - now;

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((difference % (1000 * 60)) / 1000),
        });
        setIsExpired(false);
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        setIsExpired(true);
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [promotion.end_date]);

  if (!promotion.is_active || isExpired) return null;

  const getDiscountText = () => {
    if (promotion.discount_percentage) return `${promotion.discount_percentage}% OFF`;
    if (promotion.discount_amount) return `$${promotion.discount_amount} OFF`;
    return "OFERTA";
  };

  const getUrgencyLevel = () => {
    const totalHours = timeLeft.days * 24 + timeLeft.hours;
    if (totalHours <= 24) return "urgent";
    if (totalHours <= 72) return "medium";
    return "normal";
  };

  const urgencyLevel = getUrgencyLevel();
  const urgencyColors = {
    urgent: "bg-gradient-to-r from-red-500 to-pink-500 border-red-300",
    medium: "bg-gradient-to-r from-amber-500 to-orange-500 border-amber-300",
    normal: "bg-gradient-to-r from-primary to-accent border-primary/30"
  };

  return (
    <Card 
      className={`overflow-hidden border-2 ${urgencyColors[urgencyLevel]} text-white shadow-lg transition-all duration-700 ease-out ${
        isVisible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-4 scale-95"
      }`}
    >
      {promotion.image_url && (
        <div className="relative h-32 w-full overflow-hidden">
          <img
            src={promotion.image_url}
            alt={promotion.title}
            className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
          />
          <div className="absolute inset-0 bg-black/30" />
        </div>
      )}
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              {promotion.discount_percentage && (
                <Badge variant="secondary" className="bg-white/20 text-white border-white/30 animate-pulse">
                  <Percent className="h-3 w-3 mr-1" />
                  {getDiscountText()}
                </Badge>
              )}
              {promotion.discount_amount && (
                <Badge variant="secondary" className="bg-white/20 text-white border-white/30 animate-pulse">
                  <DollarSign className="h-3 w-3 mr-1" />
                  {getDiscountText()}
                </Badge>
              )}
            </div>
            <h3 className="font-bold text-lg mb-1">{promotion.title}</h3>
            {promotion.description && <p className="text-white/90 text-sm">{promotion.description}</p>}
          </div>
        </div>

        <div className="bg-white/20 rounded-lg p-3 backdrop-blur-sm">
          <div className="flex items-center gap-2 mb-2">
            <Clock className={`h-4 w-4 ${urgencyLevel === "urgent" ? "animate-bounce" : ""}`} />
            <span className="text-sm font-medium">
              {urgencyLevel === "urgent" ? "¡Termina pronto!" : 
               urgencyLevel === "medium" ? "Tiempo limitado" : "Disponible por"}
            </span>
          </div>
          
          <div className="grid grid-cols-4 gap-2 text-center">
            {[
              { value: timeLeft.days, label: "días" },
              { value: timeLeft.hours, label: "hrs" },
              { value: timeLeft.minutes, label: "min" },
              { value: timeLeft.seconds, label: "seg" },
            ].map((unit, i) => (
              <div key={i} className="bg-white/20 rounded p-2 backdrop-blur-sm transition-all duration-300 hover:bg-white/30">
                <div className="font-bold text-lg tabular-nums">{unit.value}</div>
                <div className="text-xs opacity-90">{unit.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
}
